// start: every element wired once the page is ready; window.ds for page scripts.
import { initGrids } from '../grid.ts';
import { initCalendars } from '../calendar-view.ts';
import { initChips } from './chips.ts';
import { confirmDialog, initConfirm } from './confirm.ts';
import { $, $$, closeLayers, icon, play } from './core.ts';
import { enhanceDate } from './date.ts';
import { initDialogs } from './dialogs.ts';
import { enhanceEditor } from './editor.ts';
import { answer, flash, showFlash } from './flash.ts';
import { form, forms, initForms } from './form.ts';
import { initIcons } from './icons.ts';
import { initDigits, initSideScroll } from './legacy.ts';
import { initMenus } from './menus.ts';
import { otpStep } from './otp-step.ts';
import { initOtp } from './otp.ts';
import { initEye } from './password-eye.ts';
import { initPwa } from './pwa.ts';
import { initRailTips } from './rail-tips.ts';
import { enhanceSelect } from './select.ts';
import { initShell } from './shell.ts';
import { table } from './table.ts';
import { initTabs } from './tabs.ts';
import { initFeedback, initSmsPreview, toast, type ToastType } from './toast.ts';
import { enhanceFile, initUpload } from './upload.ts';
import { initValidation, loadRules, validateForm, words } from './validation.ts';
import type { DsQueued } from '../globals.d.ts';

/** What page scripts get as window.ds (and the jQuery bridge forwards to). */
export interface DsApi {
  toast: typeof toast;
  confirm: typeof confirmDialog;
  icon: typeof icon;
  play: typeof play;
  words: typeof words;
  enhanceSelect: typeof enhanceSelect;
  validateForm: typeof validateForm;
  table: typeof table;
  flash: typeof flash;
  answer: typeof answer;
  enhance: typeof enhance;
  enhanceEditor: typeof enhanceEditor;
  enhanceFile: typeof enhanceFile;
  otpStep: typeof otpStep;
  form: typeof form;
  forms: typeof forms;
}

// ---------------- start ----------------
function start(): void {
  initShell();
  initSmsPreview();
  initFeedback();
  initConfirm();
  initDialogs();
  initEye();
  initOtp();
  initChips();
  initUpload();
  initIcons();
  initPwa();
  initRailTips();
  initTabs();
  initMenus();
  initDigits();
  initSideScroll();
  showFlash();
  // T-074: server data grids swap their own fragment (ds/grid.js)
  // grid.js says 'error' for a failure; the toast's own type is 'danger' (without the mapping it showed no icon and never closed)
  initGrids({
    toast: (msg, type) => toast(msg, { type: (type === 'error' ? 'danger' : type) as ToastType }),
    enhance,
    closeLayers,
  });
  // T-161: the platform calendar swaps its own fragment (calendar-view.ts)
  initCalendars({
    toast: (msg, type) => toast(msg, { type: (type === 'error' ? 'danger' : type) as ToastType }),
    enhance,
  });
  $$<HTMLSelectElement>('select[data-ds-select], .ds-content select.form-select:not([data-ds-native])').forEach(s =>
    enhanceSelect(s),
  );
  $$<HTMLInputElement>('input[data-ds-date], input[data-jdp]').forEach(enhanceDate);
  $$('[data-ds-table]').forEach(table);
  $$<HTMLTextAreaElement>('textarea.summernote, textarea[data-ds-editor]').forEach(enhanceEditor);
  $$<HTMLInputElement>('.ds-content input[type=file], dialog input[type=file]').forEach(enhanceFile);
  if ($('input[data-rule]')) {
    loadRules().then(initValidation).catch(initValidation);
  }
  // T-171: forms declared by a form definition (<ds-form>) post through the platform submitter
  initForms();
  // page scripts that ran before this module (the jQuery bridge) queued their calls

  const queued: DsQueued[] = Array.isArray(window.dsQueue) ? (window.dsQueue as DsQueued[]) : [];
  window.dsQueue = { push: (fn: DsQueued) => fn(window.ds) };
  queued.forEach(fn => {
    try {
      fn(window.ds);
    } catch (e) {
      console.error(e);
    }
  });
}
// markup added later (AJAX partials): selects, dates, tables, tabs
function enhance(root: Element | null | undefined): void {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- only checks that the argument can be searched (a page script may pass anything)
  if (!root?.querySelectorAll) return;
  $$<HTMLSelectElement>('select[data-ds-select], select.form-select:not([data-ds-native])', root).forEach(s => {
    if (s.closest('.ds-content, dialog')) enhanceSelect(s);
  });
  $$<HTMLInputElement>('input[data-ds-date], input[data-jdp]', root).forEach(enhanceDate);
  $$('[data-ds-table]', root).forEach(table);
  $$<HTMLInputElement>('input[type=file]', root).forEach(enhanceFile);
  initTabs(root);
}
window.ds = {
  toast,
  confirm: confirmDialog,
  icon,
  play,
  words,
  enhanceSelect,
  validateForm,
  table,
  flash,
  answer,
  enhance,
  enhanceEditor,
  enhanceFile,
  otpStep,
  form,
  forms,
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
else start();

// the module API of the old wwwroot/ds/ds.js, unchanged
export { icon, play } from './core.ts';
export { jalaliText } from './date.ts';
export { enhanceEditor } from './editor.ts';
export { answer, flash } from './flash.ts';
export { mountOtp } from './otp.ts';
export { otpStep } from './otp-step.ts';
export { table } from './table.ts';
export { toast } from './toast.ts';
export { enhanceFile } from './upload.ts';
export { validateForm, words } from './validation.ts';
